/**
 * Parses a string representation of a number in a given locale into a number.
 * This function handles different decimal and thousands separators, as well as different numeral systems.
 * @param stringNumber The number as a string.
 * @param locale The BCP 47 language tag for the locale.
 * @returns The parsed number. Returns NaN if parsing is not possible.
 */
export const parseLocaleNumber = (stringNumber: string, locale: string): number => {
  if (typeof stringNumber !== 'string') {
    return NaN
  }

  try {
    const formatter = new Intl.NumberFormat(locale)
    const parts = formatter.formatToParts(12345.6)

    const groupSeparator = parts.find(part => part.type === 'group')?.value
    const decimalSeparator = parts.find(part => part.type === 'decimal')?.value

    if (!decimalSeparator) {
      return parseFloat(stringNumber.replace(/[^\d-]/g, ''))
    }

    let normalized = stringNumber

    if (groupSeparator) {
      // Escape special characters for RegExp
      const escapedGroupSeparator = groupSeparator.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
      normalized = normalized.replace(new RegExp(escapedGroupSeparator, 'g'), '')
    }

    const escapedDecimalSeparator = decimalSeparator.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
    normalized = normalized.replace(new RegExp(escapedDecimalSeparator), '.')

    // Basic cleaning of any other non-numeric characters that might remain, except for the decimal point and minus sign.
    normalized = normalized.replace(/[^\d.-]/g, '')

    return parseFloat(normalized)
  } catch (e) {
    // Fallback for invalid locales or other unexpected errors
    return NaN
  }
}
